import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Grid, Paper, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, CircularProgress,
  Alert, Container, useTheme, alpha
} from '@mui/material';
import { Event as EventIcon, Add as AddIcon, CheckCircle as CheckCircleIcon, Cancel as CancelIcon, Pending as PendingIcon } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { getTenantDataService } from '../services/tenant-data-service';
import { awsUserService } from '../services/aws-user-service';
import { LeaveRequest, CompanySettings } from '../types/hr';
import { calculateLeaveDays } from '../utils/leaveCalculator';
import AnimatedPage from '../components/AnimatedPage';
import AnimatedLoader from '../components/common/AnimatedLoader';

const LeaveDashboard: React.FC = () => {
  const { currentUser, userProfile } = useAuth();
  const theme = useTheme();
  
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState<LeaveRequest[]>([]);
  const [companySettings, setCompanySettings] = useState<CompanySettings | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  
  // Application Form State
  const [openApplyDialog, setOpenApplyDialog] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [leaveType, setLeaveType] = useState<'sick' | 'casual' | 'unpaid'>('casual');
  const [reason, setReason] = useState('');
  const [applying, setApplying] = useState(false);

  const tenantId = userProfile?.tenantId || currentUser?.uid;
  const db = useMemo(() => getTenantDataService(currentUser, userProfile), [currentUser, userProfile]);
  const isAdmin = userProfile?.role === 'admin' || userProfile?.role === 'super_admin';

  useEffect(() => {
    fetchData();
  }, [tenantId]);

  const fetchData = async () => {
    if (!tenantId) return;
    try {
      setLoading(true);
      // Fetch global settings from owner
      const ownerProfile = await awsUserService.getUserProfile(tenantId);
      if (ownerProfile?.hrSettings) {
        setCompanySettings(ownerProfile.hrSettings);
      }

      // Fetch leave requests
      const allRequests = await db.getAll<LeaveRequest>('leave_requests');
      
      // If admin, see all. If staff, see own.
      if (isAdmin) {
        setRequests(allRequests.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()));
      } else {
        const ownRequests = allRequests.filter(r => r.staff_id === currentUser?.uid);
        setRequests(ownRequests.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()));
      }
    } catch (err: any) {
      console.error('Error fetching leave data:', err);
      setError('Failed to load leave dashboard data.');
    } finally {
      setLoading(false);
    }
  };

  const calculatedDays = useMemo(() => {
    if (startDate && endDate) {
      return calculateLeaveDays(startDate, endDate, companySettings);
    }
    return 0;
  }, [startDate, endDate, companySettings]);

  const handleSubmitLeave = async () => {
    if (!startDate || !endDate || calculatedDays <= 0) {
      setError('Please select valid dates. (Weekends and holidays are skipped).');
      return;
    }
    
    try {
      setApplying(true);
      const newRequest: LeaveRequest = {
        id: Math.random().toString(36).slice(2),
        staff_id: currentUser?.uid || '',
        start_date: startDate,
        end_date: endDate,
        type: leaveType,
        status: 'pending',
        total_days: calculatedDays,
        reason: reason,
        created_at: new Date().toISOString()
      };

      await db.put('leave_requests', newRequest);
      setRequests(prev => [newRequest, ...prev]);
      setOpenApplyDialog(false);
      
      // Reset form
      setStartDate('');
      setEndDate('');
      setReason('');
      setLeaveType('casual');
    } catch (err) {
      setError('Failed to submit leave request.');
    } finally {
      setApplying(false);
    }
  };

  const handleUpdateStatus = async (request: LeaveRequest, newStatus: 'approved' | 'rejected') => {
    try {
      const updated = { ...request, status: newStatus };
      await db.put('leave_requests', updated);
      setRequests(prev => prev.map(r => r.id === request.id ? updated : r));
    } catch (err) {
      console.error('Failed to update leave status:', err);
    }
  };

  // Balances
  const currentYear = new Date().getFullYear();
  const myApprovedThisYear = requests.filter(r => 
    !isAdmin && 
    r.status === 'approved' && 
    r.start_date.startsWith(currentYear.toString())
  );

  const usedCasual = myApprovedThisYear.filter(r => r.type === 'casual').reduce((s, r) => s + r.total_days, 0);
  const usedSick = myApprovedThisYear.filter(r => r.type === 'sick').reduce((s, r) => s + r.total_days, 0);
  
  const casualTotal = companySettings?.casual_leaves_per_year || 0;
  const sickTotal = companySettings?.sick_leaves_per_year || 0;

  if (loading) return <AnimatedLoader message="Loading Leave Dashboard..." />;

  return (
    <AnimatedPage>
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }}>
              <EventIcon fontSize="large" />
            </Box>
            <Box>
              <Typography variant="h4" component="h1" fontWeight="bold">
                Leave Management
              </Typography>
              <Typography variant="subtitle1" color="textSecondary">
                {isAdmin ? 'Manage staff leave requests' : 'View your leave balances and apply for time off'}
              </Typography>
            </Box>
          </Box>
          {!isAdmin && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenApplyDialog(true)}>
              Apply for Leave
            </Button>
          )}
        </Box>

        {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

        {/* Balances & Holidays (Staff Only) */}
        {!isAdmin && (
          <Grid container spacing={3} mb={4}>
            <Grid item xs={12} sm={4}>
              <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2, textAlign: 'center', height: '100%' }}>
                <Typography variant="h6" color="textSecondary" gutterBottom>Casual Leaves</Typography>
                <Typography variant="h3" fontWeight="bold" color="primary">
                  {casualTotal - usedCasual} <Typography component="span" variant="h6" color="textSecondary">/ {casualTotal} available</Typography>
                </Typography>
                <Typography variant="caption" color="textSecondary">Used {usedCasual} days this year</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2, textAlign: 'center', height: '100%' }}>
                <Typography variant="h6" color="textSecondary" gutterBottom>Sick Leaves</Typography>
                <Typography variant="h3" fontWeight="bold" color="error">
                  {sickTotal - usedSick} <Typography component="span" variant="h6" color="textSecondary">/ {sickTotal} available</Typography>
                </Typography>
                <Typography variant="caption" color="textSecondary">Used {usedSick} days this year</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2, height: '100%' }}>
                <Typography variant="h6" color="textSecondary" gutterBottom textAlign="center">Upcoming Holidays</Typography>
                <Box sx={{ mt: 2, maxHeight: 120, overflowY: 'auto' }}>
                  {!companySettings?.company_holidays || companySettings.company_holidays.filter(h => new Date(h.date) >= new Date(new Date().setHours(0,0,0,0))).length === 0 ? (
                    <Typography variant="body2" color="textSecondary" textAlign="center" mt={2}>
                      No upcoming holidays.
                    </Typography>
                  ) : (
                    companySettings.company_holidays
                      .filter(h => new Date(h.date) >= new Date(new Date().setHours(0,0,0,0)))
                      .slice(0, 5)
                      .map((holiday, idx) => (
                        <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, p: 1, bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: 1 }}>
                          <Typography variant="body2" fontWeight="bold">{holiday.name}</Typography>
                          <Typography variant="body2" color="textSecondary">{new Date(holiday.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</Typography>
                        </Box>
                      ))
                  )}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        )}

        <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <Box p={2} borderBottom="1px solid" borderColor="divider" bgcolor={alpha(theme.palette.primary.main, 0.03)}>
            <Typography variant="h6" fontWeight="bold">{isAdmin ? 'All Leave Requests' : 'My Leave Requests'}</Typography>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {isAdmin && <TableCell><strong>Staff UID</strong></TableCell>}
                  <TableCell><strong>Type</strong></TableCell>
                  <TableCell><strong>Start Date</strong></TableCell>
                  <TableCell><strong>End Date</strong></TableCell>
                  <TableCell><strong>Duration</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  {isAdmin && <TableCell align="right"><strong>Actions</strong></TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {requests.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={isAdmin ? 7 : 5} align="center" sx={{ py: 4 }}>
                      <Typography color="textSecondary">No leave requests found.</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  requests.map((row) => (
                    <TableRow key={row.id}>
                      {isAdmin && <TableCell>{row.staff_id}</TableCell>}
                      <TableCell>
                        <Chip size="small" label={row.type.toUpperCase()} color={row.type === 'sick' ? 'error' : row.type === 'casual' ? 'primary' : 'default'} />
                      </TableCell>
                      <TableCell>{row.start_date}</TableCell>
                      <TableCell>{row.end_date}</TableCell>
                      <TableCell>{row.total_days} Day(s)</TableCell>
                      <TableCell>
                        <Chip 
                          size="small" 
                          icon={row.status === 'approved' ? <CheckCircleIcon /> : row.status === 'rejected' ? <CancelIcon /> : <PendingIcon />}
                          label={row.status.toUpperCase()} 
                          color={row.status === 'approved' ? 'success' : row.status === 'rejected' ? 'error' : 'warning'} 
                          variant="outlined"
                        />
                      </TableCell>
                      {isAdmin && (
                        <TableCell align="right">
                          {row.status === 'pending' && (
                            <Box display="flex" gap={1} justifyContent="flex-end">
                              <Button size="small" variant="contained" color="success" onClick={() => handleUpdateStatus(row, 'approved')}>Approve</Button>
                              <Button size="small" variant="outlined" color="error" onClick={() => handleUpdateStatus(row, 'rejected')}>Reject</Button>
                            </Box>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Container>

      {/* Apply Leave Dialog */}
      <Dialog open={openApplyDialog} onClose={() => setOpenApplyDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Apply for Leave</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Leave Type"
                value={leaveType}
                onChange={(e) => setLeaveType(e.target.value as any)}
              >
                <MenuItem value="casual">Casual Leave</MenuItem>
                <MenuItem value="sick">Sick Leave</MenuItem>
                <MenuItem value="unpaid">Unpaid Leave</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                type="date"
                fullWidth
                label="Start Date"
                InputLabelProps={{ shrink: true }}
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                type="date"
                fullWidth
                label="End Date"
                InputLabelProps={{ shrink: true }}
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </Grid>
            
            {startDate && endDate && (
              <Grid item xs={12}>
                <Alert severity="info">
                  <strong>Smart Calculator:</strong> This request will deduct <strong>{calculatedDays} working days</strong> from your balance (excluding weekends and company holidays).
                </Alert>
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Reason (Optional)"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setOpenApplyDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleSubmitLeave} 
            variant="contained" 
            color="primary"
            disabled={applying || calculatedDays <= 0}
          >
            {applying ? 'Submitting...' : 'Submit Request'}
          </Button>
        </DialogActions>
      </Dialog>
    </AnimatedPage>
  );
};

export default LeaveDashboard;
